import React, { FC, useEffect, useState } from 'react';
import cx from 'classnames';
import styles from './submitting-page.module.scss';
import SubmittingComponent from './SubmittingComponent/SubmittingComponent';
import { getDeviceInfo, getLocation, sendDOFirebaseEvent } from '../../utils/utils';
import UnableToSubmitComponent from './UnableToSubmit/UnableToSubmitComponent';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'app/configureStore';
import { useFormNetworkCalls } from 'utils/useFormNetworkCalls';
import SuccessAnimationComponent from './SuccessAnimation/SuccessAnimationComponent';
import { AnalyticsPages, DeviceInfo, DO_APPLICATION_TYPE, KYCStatus, Location } from 'client-experience-toolbox';
import { useApplication } from 'hooks/useApplication';
import { Dialog } from 'drift-ui';
import { useUpdateApplication } from 'utils/useUpdateApplication';
import { DOFirebaseEvents } from 'app/types';

type SubmissionState = 'submitting' | 'submitted' | 'failed';
interface SubmissionStatusProps {
  submissionStatus?: SubmissionState;
  type: DO_APPLICATION_TYPE;
  submissionStep: number;
}

const SubmissionStatus: FC<SubmissionStatusProps> = ({ submissionStatus, type, submissionStep }) => {
  if (type === DO_APPLICATION_TYPE.FACTORING) {
    if (submissionStatus === 'submitting') {
      return <SubmittingComponent type={type} />;
    } else if (submissionStatus === 'submitted') {
      return <SuccessAnimationComponent />;
    }

    return <UnableToSubmitComponent />;
  }

  if (type === DO_APPLICATION_TYPE.BANKING) {
    return (
      <Dialog open aria-label="Submitting application">
        <SubmittingComponent type={type} postSubmissionStep={submissionStep} />
      </Dialog>
    );
  }

  return null;
};

const SubmissionPage: FC<{ type: DO_APPLICATION_TYPE }> = ({ type }) => {
  const { application } = useApplication(type);
  const primaryApplicant = application?.applicationData?.applicants?.find(
    (applicant) => applicant.applicantId === application?.primaryApplicantId,
  );
  const submissionStatus = useSelector((state: RootState) => state.applicationReducer.submissionStatus);
  const submissionStep = useSelector((state: RootState) => state.applicationReducer.submissionStep);
  const [submitPayload, setSubmitPayload] = useState<{ deviceInfo: DeviceInfo; location: Location } | null>(null);
  const { submitApplication, submitBackgroundCheck } = useFormNetworkCalls(type);
  const updateApplication = useUpdateApplication(type);
  const dispatch = useDispatch();
  const isBanking = type === DO_APPLICATION_TYPE.BANKING;

  useEffect(() => {
    const getPayload = async () => {
      const deviceInfo = getDeviceInfo();
      const locationData = await getLocation();

      setSubmitPayload({
        deviceInfo,
        location: locationData,
      });
    };

    if (submissionStep > 1 || submissionStatus === 'failed' || submitPayload !== null) return;
    getPayload();
  }, [submissionStatus, submitPayload, submissionStep]);

  useEffect(() => {
    const submit = async () => {
      if (submitPayload === null || submissionStatus === 'failed') return;

      if (!application?._id) {
        console.error('Application ID is not available');
        dispatch({ type: 'SET_SUBMISSION_STATUS', submissionStatus: 'failed' });
        return;
      }

      try {
        await submitApplication({
          deviceInfo: submitPayload.deviceInfo,
          location: submitPayload.location,
        });
        if (!isBanking) {
          sendDOFirebaseEvent(
            DOFirebaseEvents.FACTORING_APPLICATION_SUBMISSION,
            AnalyticsPages.FACTORING_APPLICATION_FORM_SIGN_AND_SUBMIT,
            {
              description: 'Factoring Application Submitted',
              applicationType: DO_APPLICATION_TYPE.FACTORING,
              applicationId: application?._id ?? 'unknown',
              user_email: primaryApplicant?.email ?? 'unknown',
            },
          );
          // Non-Banking applications will mark as submitted and move on to the Submission Success/Failure page
          dispatch({ type: 'SET_SUBMISSION_STATUS', submissionStatus: 'submitted' });
        } else {
          // Banking Applicaitons will update the submission step to trigger background checks
          dispatch({ type: 'SET_POST_SUBMISSION_STEP', submissionStep: 2 });
        }
      } catch (error) {
        console.error('Error submitting application:', error);
        dispatch({ type: 'SET_SUBMISSION_STATUS', submissionStatus: 'failed' });
      }
    };

    if (!isBanking && primaryApplicant?.signedAt)
      // Non-banking application being re-visited for some reason get marked as submitted to avoid re-submitting
      dispatch({ type: 'SET_SUBMISSION_STATUS', submissionStatus: 'submitted' });
    if (submissionStatus === 'submitting' && submissionStep === 1) submit();
  }, [
    submitPayload,
    submissionStatus,
    application?._id,
    dispatch,
    primaryApplicant?.signedAt,
    submitApplication,
    isBanking,
    submissionStep,
  ]);

  useEffect(() => {
    const submitKYC = async () => {
      try {
        if (application?._id) {
          const kycResponse = await submitBackgroundCheck(application?._id, 'kyc');
          const submissionSteps = kycResponse?.submissionSteps;
          if (submissionSteps?.kycStatus !== KYCStatus.APPROVED) {
            // KYC not immediately approved so we update the application with response data and move to PostSubmission for possible manual steps
            updateApplication('submissionSteps', submissionSteps);
            dispatch({ type: 'SET_POST_SUBMISSION_STEP', submissionStep: 4 });
            dispatch({ type: 'SET_SUBMISSION_STATUS', submissionStatus: 'submitted' });
          } else {
            // KYC was approved immediately, move on to KYB step
            dispatch({ type: 'SET_POST_SUBMISSION_STEP', submissionStep: 3 });
          }
        }
      } catch (error) {
        console.error('Error submitting KYC background check:', error);
        dispatch({ type: 'SET_BANKING_SUBMISSION_ERROR', bankingSubmissionError: true });
        dispatch({ type: 'SET_SUBMISSION_STATUS', submissionStatus: 'failed' });
        return null;
      }
    };

    const submitKYB = async () => {
      try {
        if (application?._id) {
          const kybResponse = await submitBackgroundCheck(application?._id, 'kyb');
          const submissionSteps = kybResponse?.submissionSteps;

          updateApplication('submissionSteps', submissionSteps);
          dispatch({ type: 'SET_POST_SUBMISSION_STEP', submissionStep: 4 });
          dispatch({ type: 'SET_SUBMISSION_STATUS', submissionStatus: 'submitted' });
        }
      } catch (error) {
        console.error('Error submitting KYB background check:', error);
        dispatch({ type: 'SET_BANKING_SUBMISSION_ERROR', bankingSubmissionError: true });
        dispatch({ type: 'SET_SUBMISSION_STATUS', submissionStatus: 'failed' });
        return null;
      }
    };

    if (submissionStep === 2) {
      submitKYC();
    } else if (submissionStep === 3) {
      submitKYB();
    }
  }, [submissionStep, application?._id, submitBackgroundCheck, updateApplication, dispatch]);

  return (
    <div className={cx(!isBanking && styles.container)}>
      <SubmissionStatus submissionStatus={submissionStatus} type={type} submissionStep={submissionStep} />
    </div>
  );
};

export default SubmissionPage;
